// src/database/providerDirectory.js

// --------------- IndexedDB usage for storing and retrieving provider directory data ---------------

const DB_NAME = "providerDirectoryDb";
const DB_VERSION = 5;
const STORE_NAME = "providers";

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      const store = db.createObjectStore(STORE_NAME, {
        keyPath: "number",
      });

      store.createIndex(
        "primaryTaxonomyDescription",
        "primaryTaxonomyDescription",
        { unique: false }
      );

      store.createIndex("providerCategoryKey", "providerCategoryKey", {
        unique: false,
      });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveProviders(providers) {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    for (const provider of providers) {
      const npiNumber = provider.number || provider.npi;

      if (!npiNumber) {
        console.warn("Skipping provider without NPI number", provider);
        continue;
      }

      store.put({
        ...provider,
        number: String(npiNumber),
      });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllProviders() {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getProvidersByCategory(providerCategoryKey) {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const index = tx.objectStore(STORE_NAME).index("providerCategoryKey");
    const request = index.getAll(providerCategoryKey);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// --------------- NPI Registry API fetching ---------------

// Call through CRA proxy to avoid CORS
const NPI_BASE_URL = `${process.env.REACT_APP_API_BASE_URI}/api/npi`;

/**
 * IMPORTANT:
 * taxonomy_description must be a NUCC taxonomy DESCRIPTION
 * (exact Classification/Specialty text, or wildcard after 2 chars),
 * NOT the taxonomy code.
 *
 * These descriptions are valid NUCC classifications:
 * - Audiologist (231H00000X)
 * - Hearing Aid Equipment (332S00000X)
 * - Hearing Instrument Specialist (237700000X)
 * - Otolaryngology (207Y00000X)
 */
const TAXONOMY_DESCRIPTIONS = {
  hearingInstrumentSpecialist: "Hearing Instrument Specialist",
  hearingAidEquipment: "Hearing Aid Equipment",
  entPhysician: "Otolaryngology",
  audiologist: "Audiologist",
  psychologist: "Psychologist", // broad NUCC classification (103T00000X)
};

function buildUrl(taxonomyDescription, skip = 0) {
  const params = new URLSearchParams({
    version: "2.1",
    state: "NE",
    country_code: "US",
    taxonomy_description: taxonomyDescription,
    limit: "200",
    skip: String(skip),
  });

  return `${NPI_BASE_URL}?${params.toString()}`;
}

async function fetchAllForTaxonomyDescription(taxonomyDescription) {
  let results = [];
  let allResults = [];
  let skip = 0;

  // eslint-disable-next-line no-constant-condition
  while (results.length < 200) {
    const url = buildUrl(taxonomyDescription, skip);
    const res = await fetch(url);

    

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`NPI API error ${res.status}: ${text}`);
    }

    const data = await res.json();

    if (data.Errors && data.Errors.length > 0) {
      console.error(
        "NPI API errors for taxonomy_description:",
        taxonomyDescription,
        data.Errors
      );
      throw new Error("NPI API returned errors");
    }

    results = data.results || [];
    console.log("URL", url);
    console.log("RAW DATA", data);
    console.log("RESULT COUNT", data.results?.length || 0);
    if (results.length === 0) break;

    allResults = allResults.concat(results);

    skip += 200;
  }

  return allResults;
}

export async function fetchAllNebraskaProviders() {
  const entries = Object.entries(TAXONOMY_DESCRIPTIONS);

  const allByCategory = await Promise.all(
    entries.map(async ([key, description]) => {
      try {
        const providers = await fetchAllForTaxonomyDescription(description);

        return providers.map((p) => ({
          ...p,
          providerCategoryKey: key,
          primaryTaxonomyDescription: description,
        }));
      } catch (e) {
        console.warn(`Skipping taxonomy ${description} due to NPI error`, e);
        return [];
      }
    })
  );

  return allByCategory.flat();
}
